import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { checkAuth } from './features/auth/authSlice';
import { RootState, AppDispatch } from './app/store';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/layout/Navbar';
import LoginPage from './features/auth/LoginPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import DashboardPage from './features/dashboard/DashboardPage';
import RequisitionListPage from './features/requisitions/RequisitionListPage';
import RequisitionDetailPage from './features/requisitions/RequisitionDetailPage';
import AdminSessionDashboard from './features/admin/AdminSessionDashboard';
import { setIntendedUrl, shouldStoreAsIntendedUrl } from './utils/navigation';
import { devLog } from './utils/logger';
import { useActivityTracker } from './hooks/useActivityTracker';
import InstallPrompt from './components/common/InstallPrompt';

// A simple PrivateRoute component definition within App.tsx for now
// You might move this to its own file e.g., src/components/auth/PrivateRoute.tsx
const PrivateRoute = ({ children }: { children: React.ReactElement }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const location = useLocation();

  // You might also want to check if auth state is still loading here in a more complex setup
  // const isLoadingAuth = useSelector((state: RootState) => state.auth.isLoading);
  // if (isLoadingAuth) return <div>Loading authentication...</div>;

  if (!isAuthenticated) {
    devLog.debug('🔒 User not authenticated, checking if should store intended URL for:', location.pathname);
    // Store the current location as intended URL if it should be stored
    if (shouldStoreAsIntendedUrl(location.pathname)) {
      setIntendedUrl(location.pathname);
    }
    devLog.debug('🔄 Redirecting to login page');
    return <Navigate to="/login" replace />;
  }

  devLog.debug('✅ User authenticated, rendering protected content for:', location.pathname);
  return children;
};

// Admin route component that checks for admin privileges
const AdminRoute = ({ children }: { children: React.ReactElement }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();

  if (!isAuthenticated) {
    devLog.debug('🔒 User not authenticated, checking if should store intended URL for:', location.pathname);
    if (shouldStoreAsIntendedUrl(location.pathname)) {
      setIntendedUrl(location.pathname);
    }
    devLog.debug('🔄 Redirecting to login page');
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin (assuming there's an is_admin field or similar)
  // You might need to adjust this based on your user model structure
  const isAdmin = user && (user as any).is_admin; // Type assertion since we don't have is_admin in AuthUser interface

  if (!isAdmin) {
    devLog.debug('🚫 User not admin, redirecting to dashboard');
    return <Navigate to="/" replace />;
  }

  devLog.debug('✅ Admin user authenticated, rendering admin content for:', location.pathname);
  return children;
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : true; // Default to true (dark mode)
  });

  const dispatch: AppDispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);

  // Initialize activity tracker
  useActivityTracker();

  // Check for existing authentication on app load
  useEffect(() => {
    const performAuthCheck = async () => {
      // Only check if user is not already authenticated and not on login page
      const isOnLoginPage = window.location.pathname === '/login';
      if (!isAuthenticated && !isOnLoginPage) {
        devLog.debug('🔍 Checking for existing authentication...');
        try {
          await dispatch(checkAuth()).unwrap();
          devLog.debug('✅ Auto-login successful');
        } catch (error) {
          devLog.debug('❌ Auto-login failed or user not authenticated in main app');
        }
      } else if (isOnLoginPage) {
        devLog.debug('🔍 Skipping auth check on login page to avoid CORS issues');
      }
      setAuthCheckComplete(true);
    };

    performAuthCheck();
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prevMode: boolean) => !prevMode);
  }, []);

  // Show loading screen only while checking authentication initially
  if (!authCheckComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.12),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_40%),linear-gradient(160deg,#070c17,#0f1727_40%,#111936_75%)] text-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400 mx-auto"></div>
          <p className="mt-4 text-slate-200/80">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-midnight-950 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
        <Navbar isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
        <Toaster
          position="top-right"
          toastOptions={{
            className: 'shadow-lg shadow-black/20',
            duration: 5000,
            style: {
              background: isDarkMode ? '#0f1727' : '#ffffff',
              color: isDarkMode ? '#f8fafc' : '#0f1727',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
              fontFamily: '"Work Sans", system-ui',
            },
            success: {
              duration: 3200,
              iconTheme: {
                primary: '#1f9f84',
                secondary: '#ffffff',
              },
            },
            error: {
              duration: 4200,
              iconTheme: {
                primary: '#f14f30',
                secondary: '#ffffff',
              },
            }
          }}
        />
        <div className="pt-6 md:pt-4 pb-10">
          <div className="max-w-7xl mx-auto px-1 sm:px-1 lg:px-8">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route
                path="/requisitions"
                element={
                  <PrivateRoute>
                    <RequisitionListPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/requisitions/:id"
                element={
                  <PrivateRoute>
                    <RequisitionDetailPage />
                  </PrivateRoute>
                }
              />
              {/* Admin routes */}
              <Route
                path="/admin/sessions"
                element={
                  <AdminRoute>
                    <AdminSessionDashboard />
                  </AdminRoute>
                }
              />
              {/* Default route: redirect to dashboard if logged in, or login if not */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
      <InstallPrompt />
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
